/**
 * BoardCanvas — DOM-based infinite canvas with CSS transforms for pan/zoom.
 * Each shape is a positioned div. Pointer events handle drag, resize, select.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useBoard } from './useBoard'
import { ShapeCard } from './ShapeCard'
import type { BoardShape, DrawPoint } from './types'
import type { ShapeSubType } from './BoardBottomBar'

// ── Handle positions (8 handles: 4 corners + 4 edges) ──────────────────────

type HandleDir = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w'

const HANDLE_SIZE = 8

const HANDLE_CURSORS: Record<HandleDir, string> = {
  nw: 'nwse-resize', n: 'ns-resize', ne: 'nesw-resize', e: 'ew-resize',
  se: 'nwse-resize', s: 'ns-resize', sw: 'nesw-resize', w: 'ew-resize',
}

function handlePositions(w: number, h: number): { dir: HandleDir; left: number; top: number }[] {
  const hs = HANDLE_SIZE
  return [
    { dir: 'nw', left: -hs / 2, top: -hs / 2 },
    { dir: 'n', left: w / 2 - hs / 2, top: -hs / 2 },
    { dir: 'ne', left: w - hs / 2, top: -hs / 2 },
    { dir: 'e', left: w - hs / 2, top: h / 2 - hs / 2 },
    { dir: 'se', left: w - hs / 2, top: h - hs / 2 },
    { dir: 's', left: w / 2 - hs / 2, top: h - hs / 2 },
    { dir: 'sw', left: -hs / 2, top: h - hs / 2 },
    { dir: 'w', left: -hs / 2, top: h / 2 - hs / 2 },
  ]
}

// ── Segment-segment intersection (CCW test) ─────────────────────────────────

function ccw(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): boolean {
  return (cy - ay) * (bx - ax) > (by - ay) * (cx - ax)
}

function segmentsIntersect(
  ax: number, ay: number, bx: number, by: number,
  cx: number, cy: number, dx: number, dy: number,
): boolean {
  return (
    ccw(ax, ay, cx, cy, dx, dy) !== ccw(bx, by, cx, cy, dx, dy) &&
    ccw(ax, ay, bx, by, cx, cy) !== ccw(ax, ay, bx, by, dx, dy)
  )
}

// ── BoardCanvas component ───────────────────────────────────────────────────

interface CanvasProps {
  activeTool?: string
  shapeSubType?: ShapeSubType
}

export function BoardCanvas({ activeTool = 'select', shapeSubType = 'rect' }: CanvasProps) {
  const shapes = useBoard((s) => s.shapes)
  const selectedIds = useBoard((s) => s.selectedIds)
  const viewport = useBoard((s) => s.viewport)
  const setViewport = useBoard((s) => s.setViewport)
  const moveShape = useBoard((s) => s.moveShape)
  const resizeShape = useBoard((s) => s.resizeShape)
  const selectShape = useBoard((s) => s.selectShape)
  const clearSelection = useBoard((s) => s.clearSelection)
  const addShape = useBoard((s) => s.addShape)
  const updateShape = useBoard((s) => s.updateShape)
  const deleteShape = useBoard((s) => s.deleteShape)
  const currentStyle = useBoard((s) => s.currentStyle)

  const containerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const isPanningRef = useRef(false)
  const spaceDownRef = useRef(false)
  const panStartRef = useRef({ cx: 0, cy: 0, vx: 0, vy: 0 })

  // ── Keyboard: space toggle, delete ────────────────────────────────────

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault()
        spaceDownRef.current = true
      }
      if (e.code === 'Delete' || e.code === 'Backspace') {
        // avoid deleting when typing in an input or contentEditable
        const tag = (e.target as HTMLElement).tagName
        const isEditable = (e.target as HTMLElement).isContentEditable
        if (tag === 'INPUT' || tag === 'TEXTAREA' || isEditable) return
        selectedIds.forEach((id) => deleteShape(id))
      }
    }
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') spaceDownRef.current = false
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
    }
  }, [selectedIds, deleteShape])

  // ── Animated viewport (momentum + smooth zoom) ───────────────────────

  const animVpRef = useRef({ x: viewport.x, y: viewport.y, zoom: viewport.zoom })
  const velocityRef = useRef({ vx: 0, vy: 0 })
  const animFrameRef = useRef<number>(0)

  // Smooth animation loop — decays velocity for momentum
  const startMomentum = useCallback(() => {
    if (animFrameRef.current) return
    const tick = () => {
      const v = velocityRef.current
      const decay = 0.92
      v.vx *= decay
      v.vy *= decay

      if (Math.abs(v.vx) > 0.1 || Math.abs(v.vy) > 0.1) {
        animVpRef.current.x += v.vx
        animVpRef.current.y += v.vy
        setViewport({ x: animVpRef.current.x, y: animVpRef.current.y })
        animFrameRef.current = requestAnimationFrame(tick)
      } else {
        v.vx = 0
        v.vy = 0
        animFrameRef.current = 0
      }
    }
    animFrameRef.current = requestAnimationFrame(tick)
  }, [setViewport])

  // Keep animVpRef in sync with store viewport
  useEffect(() => {
    animVpRef.current = { x: viewport.x, y: viewport.y, zoom: viewport.zoom }
  }, [viewport.x, viewport.y, viewport.zoom])

  // Cleanup animation on unmount
  useEffect(() => () => { if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current) }, [])

  // ── Wheel → zoom (pinch) or pan (two-finger scroll) ────────────────

  const onWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const rect = containerRef.current!.getBoundingClientRect()
      const cx = e.clientX - rect.left
      const cy = e.clientY - rect.top

      if (e.ctrlKey || e.metaKey) {
        // Pinch-to-zoom (or Ctrl+wheel)
        const oldZoom = viewport.zoom
        // Smaller factor for smoother pinch zoom
        const delta = -e.deltaY * 0.01
        const newZoom = Math.min(5, Math.max(0.1, oldZoom * (1 + delta)))

        const wx = (cx - viewport.x) / oldZoom
        const wy = (cy - viewport.y) / oldZoom
        const nx = cx - wx * newZoom
        const ny = cy - wy * newZoom

        animVpRef.current = { x: nx, y: ny, zoom: newZoom }
        setViewport({ x: nx, y: ny, zoom: newZoom })
      } else {
        // Two-finger pan (trackpad) with momentum
        const dx = -e.deltaX
        const dy = -e.deltaY
        animVpRef.current.x += dx
        animVpRef.current.y += dy
        velocityRef.current.vx = dx * 0.5
        velocityRef.current.vy = dy * 0.5
        setViewport({ x: animVpRef.current.x, y: animVpRef.current.y })
        startMomentum()
      }
    },
    [viewport, setViewport, startMomentum],
  )

  // ── Prevent browser zoom on the container ───────────────────────────

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    // Native wheel listener with passive:false to allow preventDefault
    const handler = (e: WheelEvent) => { e.preventDefault() }
    el.addEventListener('wheel', handler, { passive: false })
    return () => el.removeEventListener('wheel', handler)
  }, [])

  // ── Client → world coordinate conversion ────────────────────────────

  const clientToWorld = useCallback(
    (clientX: number, clientY: number): { wx: number; wy: number } => {
      const rect = containerRef.current!.getBoundingClientRect()
      const wx = (clientX - rect.left - viewport.x) / viewport.zoom
      const wy = (clientY - rect.top - viewport.y) / viewport.zoom
      return { wx, wy }
    },
    [viewport],
  )

  // ── Eraser wipe-to-delete refs ────────────────────────────────────
  const ERASER_RADIUS = 20 // world units — shapes within this radius of cursor get deleted
  const eraserActiveRef = useRef(false)
  const eraserLastPt = useRef<{ wx: number; wy: number } | null>(null)
  const eraserTrailRef = useRef<{ x: number; y: number }[]>([])
  const [eraserTrail, setEraserTrail] = useState<{ x: number; y: number }[]>([])
  const [eraserPos, setEraserPos] = useState<{ x: number; y: number } | null>(null)

  // ── Laser refs ──────────────────────────────────────────────────────
  const laserActiveRef = useRef(false)
  const laserTrailRef = useRef<{ x: number; y: number; t: number }[]>([])
  const [laserTrail, setLaserTrail] = useState<{ x: number; y: number; t: number }[]>([])
  const laserRafRef = useRef<number | null>(null)

  // ── Tool operation ref (draw, shape-rect, arrow, line, frame) ─────

  const toolOpRef = useRef<{
    type: 'draw' | 'rect' | 'arrow' | 'line' | 'frame'
    shapeId: string
    startWX: number
    startWY: number
    points?: DrawPoint[]
  } | null>(null)

  // ── Image file input handler ───────────────────────────────────────

  const imageClickPos = useRef<{ wx: number; wy: number }>({ wx: 0, wy: 0 })

  const onFileSelected = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        const src = reader.result as string
        const img = new Image()
        img.onload = () => {
          const maxW = 400
          const scale = img.width > maxW ? maxW / img.width : 1
          const w = img.width * scale
          const h = img.height * scale
          addShape({
            type: 'image',
            x: imageClickPos.current.wx,
            y: imageClickPos.current.wy,
            w,
            h,
            props: { src, alt: file.name },
          })
        }
        img.src = src
      }
      reader.readAsDataURL(file)
      e.target.value = ''
    },
    [addShape],
  )

  // ── Tool pointer-down on canvas (empty space) ─────────────────────

  const onToolPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 || spaceDownRef.current) return
      const { wx, wy } = clientToWorld(e.clientX, e.clientY)

      switch (activeTool) {
        case 'draw': {
          const id = addShape({
            type: 'draw',
            x: wx,
            y: wy,
            w: 1,
            h: 1,
            props: { points: [{ x: 0, y: 0 }], color: currentStyle.color, strokeWidth: currentStyle.strokeWidth },
          })
          toolOpRef.current = { type: 'draw', shapeId: id, startWX: wx, startWY: wy, points: [{ x: 0, y: 0 }] }
          containerRef.current!.setPointerCapture(e.pointerId)
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'text': {
          addShape({
            type: 'text',
            x: wx,
            y: wy,
            w: 200,
            h: 40,
            props: { text: '', fontSize: currentStyle.fontSize, color: currentStyle.color },
          })
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'shape': {
          const fillValue = currentStyle.fill === 'semi' ? (currentStyle.color + '40') : currentStyle.fill === 'solid' ? currentStyle.color : 'transparent'
          const dashArray = currentStyle.dash === 'dashed' ? '8 4' : currentStyle.dash === 'dotted' ? '2 4' : ''
          const id = addShape({
            type: 'shape',
            x: wx,
            y: wy,
            w: 1,
            h: 1,
            props: { shapeType: shapeSubType, fill: fillValue, stroke: currentStyle.color, strokeWidth: currentStyle.strokeWidth, dash: dashArray },
          })
          toolOpRef.current = { type: 'rect', shapeId: id, startWX: wx, startWY: wy }
          containerRef.current!.setPointerCapture(e.pointerId)
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'image': {
          imageClickPos.current = { wx, wy }
          fileInputRef.current?.click()
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'arrow': {
          const dashArray = currentStyle.dash === 'dashed' ? '8 4' : currentStyle.dash === 'dotted' ? '2 4' : ''
          const id = addShape({
            type: 'arrow',
            x: wx,
            y: wy,
            w: 1,
            h: 1,
            props: { x2: 0, y2: 0, color: currentStyle.color, strokeWidth: currentStyle.strokeWidth, curvature: 0, dash: dashArray },
          })
          toolOpRef.current = { type: 'arrow', shapeId: id, startWX: wx, startWY: wy }
          containerRef.current!.setPointerCapture(e.pointerId)
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'frame': {
          const id = addShape({
            type: 'frame',
            x: wx,
            y: wy,
            w: 1,
            h: 1,
            props: { title: 'Frame', borderColor: '#999' },
          })
          toolOpRef.current = { type: 'frame', shapeId: id, startWX: wx, startWY: wy }
          containerRef.current!.setPointerCapture(e.pointerId)
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'line': {
          const dashArrayLine = currentStyle.dash === 'dashed' ? '8 4' : currentStyle.dash === 'dotted' ? '2 4' : ''
          const id = addShape({
            type: 'line',
            x: wx,
            y: wy,
            w: 1,
            h: 1,
            props: { x2: 0, y2: 0, color: currentStyle.color, strokeWidth: currentStyle.strokeWidth, dash: dashArrayLine },
          })
          toolOpRef.current = { type: 'line', shapeId: id, startWX: wx, startWY: wy }
          containerRef.current!.setPointerCapture(e.pointerId)
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'eraser': {
          eraserActiveRef.current = true
          eraserLastPt.current = { wx, wy }
          eraserTrailRef.current = [{ x: e.clientX, y: e.clientY }]
          setEraserTrail([{ x: e.clientX, y: e.clientY }])
          setEraserPos({ x: e.clientX, y: e.clientY })
          containerRef.current!.setPointerCapture(e.pointerId)
          e.preventDefault()
          e.stopPropagation()
          break
        }
        case 'laser': {
          laserActiveRef.current = true
          const now = Date.now()
          laserTrailRef.current = [{ x: e.clientX, y: e.clientY, t: now }]
          setLaserTrail([{ x: e.clientX, y: e.clientY, t: now }])
          containerRef.current!.setPointerCapture(e.pointerId)
          e.preventDefault()
          e.stopPropagation()
          break
        }
        default:
          break
      }
    },
    [activeTool, addShape, clientToWorld, shapeSubType],
  )

  // ── Eraser: bounding-box line-segment intersection check ─────────
  const segIntersectsRect = useCallback(
    (ax: number, ay: number, bx: number, by: number, rx: number, ry: number, rw: number, rh: number): boolean => {
      // Check if line segment (ax,ay)-(bx,by) intersects rectangle (rx,ry,rw,rh)
      // Quick: if either endpoint is inside rect
      if (ax >= rx && ax <= rx + rw && ay >= ry && ay <= ry + rh) return true
      if (bx >= rx && bx <= rx + rw && by >= ry && by <= ry + rh) return true
      // Check segment against 4 edges of rect
      const edges: [number, number, number, number][] = [
        [rx, ry, rx + rw, ry], [rx + rw, ry, rx + rw, ry + rh],
        [rx + rw, ry + rh, rx, ry + rh], [rx, ry + rh, rx, ry],
      ]
      for (const [ex1, ey1, ex2, ey2] of edges) {
        if (segmentsIntersect(ax, ay, bx, by, ex1, ey1, ex2, ey2)) return true
      }
      return false
    },
    [],
  )

  // ── Tool pointer-move (draw, shape, arrow, line, frame, eraser, laser) ──

  const onToolPointerMove = useCallback(
    (e: React.PointerEvent) => {
      // Eraser wipe — radius-based: delete shapes whose bounding box is within ERASER_RADIUS of cursor
      if (eraserActiveRef.current) {
        const { wx, wy } = clientToWorld(e.clientX, e.clientY)
        const r = ERASER_RADIUS
        const prev = eraserLastPt.current

        const shapeArr = Array.from(shapes.values())
        for (const s of shapeArr) {
          // Expand bounding box by eraser radius for easier hitting
          const expanded = { x: s.x - r, y: s.y - r, w: s.w + r * 2, h: s.h + r * 2 }
          // Check: cursor point inside expanded bbox, OR segment from prev crosses expanded bbox
          const pointInside = wx >= expanded.x && wx <= expanded.x + expanded.w
            && wy >= expanded.y && wy <= expanded.y + expanded.h
          const segCrosses = prev
            ? segIntersectsRect(prev.wx, prev.wy, wx, wy, expanded.x, expanded.y, expanded.w, expanded.h)
            : false
          if (pointInside || segCrosses) {
            deleteShape(s.id)
          }
        }

        eraserLastPt.current = { wx, wy }
        eraserTrailRef.current.push({ x: e.clientX, y: e.clientY })
        if (eraserTrailRef.current.length > 60) eraserTrailRef.current = eraserTrailRef.current.slice(-40)
        setEraserTrail([...eraserTrailRef.current])
        setEraserPos({ x: e.clientX, y: e.clientY })
        return
      }

      // Laser trail
      if (laserActiveRef.current) {
        const now = Date.now()
        laserTrailRef.current.push({ x: e.clientX, y: e.clientY, t: now })
        if (laserTrailRef.current.length > 100) laserTrailRef.current = laserTrailRef.current.slice(-80)
        setLaserTrail([...laserTrailRef.current])
        return
      }

      const op = toolOpRef.current
      if (!op) return
      const { wx, wy } = clientToWorld(e.clientX, e.clientY)

      if (op.type === 'draw' && op.points) {
        const relX = wx - op.startWX
        const relY = wy - op.startWY
        op.points.push({ x: relX, y: relY })
        let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
        for (const p of op.points) {
          if (p.x < minX) minX = p.x
          if (p.y < minY) minY = p.y
          if (p.x > maxX) maxX = p.x
          if (p.y > maxY) maxY = p.y
        }
        const bw = maxX - minX || 1
        const bh = maxY - minY || 1
        const newPoints = op.points.map((p) => ({ x: p.x - minX, y: p.y - minY }))
        updateShape(op.shapeId, {
          x: op.startWX + minX,
          y: op.startWY + minY,
          w: bw,
          h: bh,
          props: { points: newPoints, color: currentStyle.color, strokeWidth: currentStyle.strokeWidth },
        })
      } else if (op.type === 'rect' || op.type === 'frame') {
        const dx = wx - op.startWX
        const dy = wy - op.startWY
        const x = dx >= 0 ? op.startWX : wx
        const y = dy >= 0 ? op.startWY : wy
        const w = Math.max(20, Math.abs(dx))
        const h = Math.max(20, Math.abs(dy))
        updateShape(op.shapeId, { x, y, w, h })
      } else if (op.type === 'arrow' || op.type === 'line') {
        const relX = wx - op.startWX
        const relY = wy - op.startWY
        const minX = Math.min(0, relX)
        const minY = Math.min(0, relY)
        const maxX = Math.max(0, relX)
        const maxY = Math.max(0, relY)
        updateShape(op.shapeId, {
          x: op.startWX + minX,
          y: op.startWY + minY,
          w: maxX - minX || 1,
          h: maxY - minY || 1,
          props: { x2: relX - minX, y2: relY - minY, color: '#333', strokeWidth: 2 },
        })
      }
    },
    [clientToWorld, updateShape, shapes, deleteShape, segIntersectsRect],
  )

  // ── Tool pointer-up (finalize draw, shape, arrow, eraser, laser) ──

  const onToolPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (toolOpRef.current) {
        containerRef.current?.releasePointerCapture(e.pointerId)
        toolOpRef.current = null
      }
      // Eraser finalize
      if (eraserActiveRef.current) {
        eraserActiveRef.current = false
        eraserLastPt.current = null
        eraserTrailRef.current = []
        setEraserTrail([])
        setEraserPos(null)
        containerRef.current?.releasePointerCapture(e.pointerId)
      }
      // Laser finalize — fade out
      if (laserActiveRef.current) {
        laserActiveRef.current = false
        containerRef.current?.releasePointerCapture(e.pointerId)
        // Animate fade-out over 600ms
        const fadeStart = Date.now()
        const fadeDuration = 600
        const trail = [...laserTrailRef.current]
        const animate = () => {
          const elapsed = Date.now() - fadeStart
          if (elapsed >= fadeDuration) {
            laserTrailRef.current = []
            setLaserTrail([])
            return
          }
          // Update trail — will fade via opacity in render
          setLaserTrail(trail.map((p) => ({ ...p, t: p.t - elapsed })))
          laserRafRef.current = requestAnimationFrame(animate)
        }
        laserRafRef.current = requestAnimationFrame(animate)
      }
    },
    [],
  )

  // Clean up laser animation on unmount
  useEffect(() => {
    return () => {
      if (laserRafRef.current) cancelAnimationFrame(laserRafRef.current)
    }
  }, [])

  // ── Pointer down on container (pan or click-to-deselect) ──────────────

  const onContainerPointerDown = useCallback(
    (e: React.PointerEvent) => {
      // Middle mouse, space+left, or pan tool → pan
      if (e.button === 1 || (e.button === 0 && spaceDownRef.current) || (e.button === 0 && activeTool === 'pan')) {
        e.preventDefault()
        isPanningRef.current = true
        panStartRef.current = { cx: e.clientX, cy: e.clientY, vx: viewport.x, vy: viewport.y }
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
        return
      }
      // Tool-specific actions on empty canvas
      if (e.button === 0 && activeTool !== 'select') {
        onToolPointerDown(e)
      }
    },
    [viewport, activeTool, onToolPointerDown],
  )

  const panLastRef = useRef({ x: 0, y: 0, t: 0 })

  const onContainerPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (isPanningRef.current) {
        const dx = e.clientX - panStartRef.current.cx
        const dy = e.clientY - panStartRef.current.cy
        const newX = panStartRef.current.vx + dx
        const newY = panStartRef.current.vy + dy
        // Track velocity for momentum
        const now = performance.now()
        const dt = now - panLastRef.current.t || 16
        panLastRef.current = {
          x: (newX - animVpRef.current.x) / dt * 16,
          y: (newY - animVpRef.current.y) / dt * 16,
          t: now,
        }
        animVpRef.current.x = newX
        animVpRef.current.y = newY
        setViewport({ x: newX, y: newY })
        return
      }
    },
    [setViewport],
  )

  const onContainerPointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (isPanningRef.current) {
        isPanningRef.current = false
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
        // Apply momentum from pan velocity
        const v = panLastRef.current
        if (Math.abs(v.x) > 0.5 || Math.abs(v.y) > 0.5) {
          velocityRef.current.vx = v.x
          velocityRef.current.vy = v.y
          startMomentum()
        }
        return
      }
      // Click on empty space → clear selection
      if (e.button === 0 && e.target === containerRef.current) {
        clearSelection()
      }
    },
    [clearSelection, startMomentum],
  )

  // ── Drag shape ────────────────────────────────────────────────────────

  const dragRef = useRef<{
    id: string; startX: number; startY: number; origX: number; origY: number
  } | null>(null)

  const onShapePointerDown = useCallback(
    (e: React.PointerEvent, shape: BoardShape) => {
      if (spaceDownRef.current) return // let pan take over
      e.stopPropagation()
      e.preventDefault()

      if (e.shiftKey) {
        selectShape(shape.id, true)
      } else if (!selectedIds.has(shape.id)) {
        selectShape(shape.id, false)
      }

      dragRef.current = {
        id: shape.id,
        startX: e.clientX,
        startY: e.clientY,
        origX: shape.x,
        origY: shape.y,
      }
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    },
    [selectShape, selectedIds],
  )

  const onShapePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!dragRef.current) return
      const zoom = viewport.zoom
      const dx = (e.clientX - dragRef.current.startX) / zoom
      const dy = (e.clientY - dragRef.current.startY) / zoom
      moveShape(dragRef.current.id, dragRef.current.origX + dx, dragRef.current.origY + dy)
    },
    [moveShape, viewport.zoom],
  )

  const onShapePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (dragRef.current) {
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
        dragRef.current = null
      }
    },
    [],
  )

  // ── Double-click shape ────────────────────────────────────────────────

  const onShapeDoubleClick = useCallback((shape: BoardShape) => {
    const typeIdMap: Record<string, string> = {
      scene: 'sceneId',
      audit: 'auditId',
      doc: 'docId',
      report: 'reportId',
      note: 'noteId',
    }
    const key = typeIdMap[shape.type]
    if (key && shape.props[key]) {
      window.dispatchEvent(
        new CustomEvent('orto:embed-open', { detail: { type: shape.type, id: shape.props[key] } }),
      )
    }
  }, [])

  // ── Resize handle ─────────────────────────────────────────────────────

  const resizeRef = useRef<{
    id: string; dir: HandleDir
    startX: number; startY: number
    origX: number; origY: number; origW: number; origH: number
  } | null>(null)

  const onHandlePointerDown = useCallback(
    (e: React.PointerEvent, shape: BoardShape, dir: HandleDir) => {
      e.stopPropagation()
      e.preventDefault()
      resizeRef.current = {
        id: shape.id,
        dir,
        startX: e.clientX,
        startY: e.clientY,
        origX: shape.x,
        origY: shape.y,
        origW: shape.w,
        origH: shape.h,
      }
      ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    },
    [],
  )

  const onHandlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      const r = resizeRef.current
      if (!r) return
      const zoom = viewport.zoom
      const dx = (e.clientX - r.startX) / zoom
      const dy = (e.clientY - r.startY) / zoom
      const d = r.dir

      let newX = r.origX
      let newY = r.origY
      let newW = r.origW
      let newH = r.origH

      // horizontal
      if (d.includes('e')) newW = r.origW + dx
      if (d.includes('w')) { newW = r.origW - dx; newX = r.origX + dx }
      // vertical
      if (d.includes('s')) newH = r.origH + dy
      if (d === 'n' || d === 'nw' || d === 'ne') { newH = r.origH - dy; newY = r.origY + dy }

      // Clamp minimum
      if (newW < 100) { if (d.includes('w')) newX = r.origX + r.origW - 100; newW = 100 }
      if (newH < 80) { if (d === 'n' || d === 'nw' || d === 'ne') newY = r.origY + r.origH - 80; newH = 80 }

      moveShape(r.id, newX, newY)
      resizeShape(r.id, newW, newH)
    },
    [moveShape, resizeShape, viewport.zoom],
  )

  const onHandlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (resizeRef.current) {
        ;(e.target as HTMLElement).releasePointerCapture(e.pointerId)
        resizeRef.current = null
      }
    },
    [],
  )

  // ── Render ────────────────────────────────────────────────────────────

  const shapeArray = Array.from(shapes.values())

  return (
    <div
      ref={containerRef}
      onWheel={onWheel}
      onPointerDown={onContainerPointerDown}
      onPointerMove={(e) => {
        onContainerPointerMove(e); onShapePointerMove(e); onHandlePointerMove(e); onToolPointerMove(e)
        // Show eraser circle on hover
        if (activeTool === 'eraser' && !eraserActiveRef.current) {
          setEraserPos({ x: e.clientX, y: e.clientY })
        }
      }}
      onPointerUp={(e) => { onContainerPointerUp(e); onShapePointerUp(e); onHandlePointerUp(e); onToolPointerUp(e) }}
      onPointerLeave={() => { if (activeTool === 'eraser') setEraserPos(null) }}
      style={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        position: 'relative',
        touchAction: 'none',
        background: '#ffffff',
        backgroundImage:
          'radial-gradient(circle, #e0e0e0 1px, transparent 1px)',
        backgroundSize: `${20 * viewport.zoom}px ${20 * viewport.zoom}px`,
        backgroundPosition: `${viewport.x}px ${viewport.y}px`,
        cursor: spaceDownRef.current || isPanningRef.current ? 'grab'
          : activeTool === 'pan' ? 'grab'
          : activeTool === 'draw' ? 'crosshair'
          : activeTool === 'eraser' ? 'none'
          : activeTool === 'text' ? 'text'
          : activeTool === 'shape' ? 'crosshair'
          : activeTool === 'arrow' ? 'crosshair'
          : activeTool === 'frame' ? 'crosshair'
          : activeTool === 'line' ? 'crosshair'
          : activeTool === 'laser' ? 'crosshair'
          : activeTool === 'image' ? 'copy'
          : 'default',
      }}
    >
      {/* Transform layer */}
      <div
        style={{
          transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
          transformOrigin: '0 0',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {shapeArray.map((shape) => {
          const isSelected = selectedIds.has(shape.id)
          const isTransparent = shape.type === 'draw' || shape.type === 'arrow' || shape.type === 'line' || shape.type === 'frame'
            || (shape.type === 'shape' && shape.props.shapeType !== 'rect')
          return (
            <div
              key={shape.id}
              onPointerDown={(e) => {
                if (activeTool === 'eraser' || activeTool === 'laser') return // let canvas handle wipe/laser
                onShapePointerDown(e, shape)
              }}
              onDoubleClick={() => onShapeDoubleClick(shape)}
              style={{
                position: 'absolute',
                left: shape.x,
                top: shape.y,
                width: shape.w,
                height: shape.h,
                border: isSelected ? '2px solid #3b82f6' : isTransparent ? 'none' : '2px solid transparent',
                borderRadius: isTransparent ? 0 : 8,
                boxSizing: 'border-box',
                cursor: activeTool === 'eraser' ? 'crosshair' : 'default',
                userSelect: 'none',
                pointerEvents: 'auto',
              }}
            >
              <ShapeCard shape={shape} />

              {/* Resize handles */}
              {isSelected &&
                handlePositions(shape.w, shape.h).map((hp) => (
                  <div
                    key={hp.dir}
                    onPointerDown={(e) => onHandlePointerDown(e, shape, hp.dir)}
                    style={{
                      position: 'absolute',
                      left: hp.left,
                      top: hp.top,
                      width: HANDLE_SIZE,
                      height: HANDLE_SIZE,
                      background: '#3b82f6',
                      border: '1px solid #fff',
                      borderRadius: 2,
                      cursor: HANDLE_CURSORS[hp.dir],
                      zIndex: 10,
                    }}
                  />
                ))}
            </div>
          )
        })}
      </div>

      {/* Eraser radius circle + trail overlay */}
      {(eraserPos || eraserTrail.length > 1) && (
        <svg
          style={{
            position: 'fixed', top: 0, left: 0,
            width: '100vw', height: '100vh',
            pointerEvents: 'none', zIndex: 9999,
          }}
        >
          {eraserTrail.length > 1 && (
            <polyline
              points={eraserTrail.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="rgba(239,68,68,0.35)"
              strokeWidth={ERASER_RADIUS * 2 * viewport.zoom}
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
          {eraserPos && (
            <circle
              cx={eraserPos.x}
              cy={eraserPos.y}
              r={ERASER_RADIUS * viewport.zoom}
              fill="rgba(239,68,68,0.1)"
              stroke="rgba(239,68,68,0.5)"
              strokeWidth="1.5"
            />
          )}
        </svg>
      )}

      {/* Laser trail overlay */}
      {laserTrail.length > 0 && (
        <svg
          style={{
            position: 'fixed', top: 0, left: 0,
            width: '100vw', height: '100vh',
            pointerEvents: 'none', zIndex: 9999,
          }}
        >
          {laserTrail.length > 1 && (
            <polyline
              points={laserTrail.map((p) => `${p.x},${p.y}`).join(' ')}
              fill="none"
              stroke="rgba(239,68,68,0.8)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{
                opacity: laserActiveRef.current ? 1 : 0.3,
                transition: laserActiveRef.current ? 'none' : 'opacity 0.5s ease-out',
              }}
            />
          )}
          {/* Red dot at last point */}
          <circle
            cx={laserTrail[laserTrail.length - 1].x}
            cy={laserTrail[laserTrail.length - 1].y}
            r="5"
            fill="rgba(239,68,68,0.9)"
            style={{
              opacity: laserActiveRef.current ? 1 : 0,
              transition: laserActiveRef.current ? 'none' : 'opacity 0.3s ease-out',
            }}
          />
        </svg>
      )}

      {/* Hidden file input for image tool */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onFileSelected}
      />
    </div>
  )
}
